import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { Spinner } from '@/components/ui';
import { ForcedPasswordChange } from './ChangePassword';

/**
 * Auth gate. Restores the session on first mount, so a page refresh lands the
 * user back where they were rather than at the sign-in screen.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const profile = useAuthStore((s) => s.profile);
  const location = useLocation();

  useEffect(() => {
    if (status === 'idle') void bootstrap();
  }, [status, bootstrap]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Spinner className="mx-auto h-6 w-6" />
          <p className="mt-3 text-sm text-ink-muted dark:text-slate-400">Loading your books…</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profile?.mustChangePassword) return <ForcedPasswordChange />;

  return <>{children}</>;
}
